import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, withCtx as _withCtx, createVNode as _createVNode, createTextVNode as _createTextVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createBlock as _createBlock, withModifiers as _withModifiers, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  id: "show-users",
  class: "d-flex flex-fill list-page"
}
const _hoisted_2 = ["id"]
const _hoisted_3 = ["id"]
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { class: "d-flex flex-row justify-content-between" }
const _hoisted_7 = { class: "col" }
const _hoisted_8 = { class: "d-flex align-items-center" }
const _hoisted_9 = { class: "btn-label" }
const _hoisted_10 = { class: "btn-text" }
const _hoisted_11 = { class: "d-flex flex-row align-items-center justify-content-center" }
const _hoisted_12 = {
  key: 0,
  class: "loader-25 margin-left-3"
}
const _hoisted_13 = ["src"]
const _hoisted_14 = { class: "pagination-wrapper mt-1" }
const _hoisted_15 = { class: "loader-div-40" }
const _hoisted_16 = ["src"]

import { onMounted, ref } from "vue";
import {
  ElTable,
  ElTableColumn,
  ElButton,
  ElCard,
  ElInput,
  ElDropdown,
  ElDropdownItem,
  ElDropdownMenu,
  ElDivider,
} from "element-plus";
import { TYPE } from "vue-toastification";
import { showAlert, showConfirm } from "@/core/popup";
import {
  deleteUser,
  getUserListing,
  getUserById,
} from "@/user/services/user.service";
import { getTenantList } from "@/tenant/services/tenant.service";
import {
  authInfo,
  hasPermission,
  updateMultiplePreference,
  updateSinglePreference,
} from "@/core/auth";
import i18n from "@/core/i18n/i18n";
import { router } from "@/core/router";
import { ASSETS_BASE_URL, DEFAULT_LOGO } from "@/core/logo";
import { getPreferenceValue } from "@/core/preferences/preferences";
import { useUserActions } from "@/user/composables/useUserActions";
import { useDebouncedSearch } from "@/core/composables/useDebouncedSearch";
import { useLatestRequestGuard } from "@/core/composables/useLatestRequestGuard";
import OneTegPagination from "@/common/components/OneTegPagination.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'Users',
  setup(__props) {

const { sendResetPassword, resetTotpAndNotify } = useUserActions();
const usersRequestGuard = useLatestRequestGuard();

const { t } = i18n.global;
const userList = ref([]);
const loadingUsers = ref(false);

const search = ref("");

const sortField = ref(
  getPreferenceValue("usersSortField")
    ? getPreferenceValue("usersSortField")
    : "createdAt"
);

const sortAscending = ref(
  getPreferenceValue("usersSortOrder")
    ? getPreferenceValue("usersSortOrder")
    : "ascending"
);

const perPageUsers = ref(
  getPreferenceValue("pageSize") ? getPreferenceValue("pageSize") : 10
);

const currentPageUsers = ref(1);

const organisations = ref([]);

const numberOfUsers = ref(0);

function sizeChanged(val) {
  perPageUsers.value = val;
  updateSinglePreference("pageSize", val);
  getUsers();
}

function sortChanged(sort) {
  sortField.value = sort.prop;
  sortAscending.value = sort.order;
  updateMultiplePreference({
    usersSortField: sort.prop,
    usersSortOrder: sort.order,
  });
  getUsers();
}

const { onSearchInput } = useDebouncedSearch(getUsers);

onMounted(() => {
  getUsers();
  getTenantList().then((response) => {
    if (response) {
      organisations.value = response;
    }
  });
});

function convertDate(dateStr) {
  const milliseconds = Date.parse(dateStr);
  const date = new Date(milliseconds);
  return date.toLocaleDateString();
}

async function getUsers() {
  const requestToken = usersRequestGuard.start();
  loadingUsers.value = true;
  const params = {
    userId: authInfo.value.userId,
    pageNumber: currentPageUsers.value - 1,
    pageSize: perPageUsers.value,
    sortField: sortField.value,
    ascending: sortAscending.value === "ascending",
    search: search.value,
  };
  // OT-4895: clear the spinner in finally so a rejected request (e.g. a 500 from
  // the listing endpoint) doesn't leave the grid stuck loading forever.
  try {
    const page = await getUserListing(params);
    // OT-4928: discard a stale response superseded by a newer search/request.
    if (!usersRequestGuard.isLatest(requestToken)) {
      return;
    }
    userList.value = page.data;
    numberOfUsers.value = page.totalElements;
  } finally {
    if (usersRequestGuard.isLatest(requestToken)) {
      loadingUsers.value = false;
    }
  }
}

function goToEditPage(row) {
  if (hasPermission("USERS.LIST.EDIT")) {
    router.push(`/users/edit/${row.id}`);
  }
}

async function deleteSelectedUser(user) {
  if (hasPermission("USERS.LIST.REMOVE")) {
    const isConfirmed = await showConfirm(
      "warning",
      t("User.DeleteUser"),
      t("User.DeleteConfirmation", { name: user.fullName })
    );
    if (isConfirmed) {
      user.loading = true;
      const response = await deleteUser(user.id);
      user.loading = false;
      if (response.code === 200) {
        showAlert(TYPE.SUCCESS, t("User.DeleteSuccess"));
        await getUsers();
      } else if (response.code === 400) {
        const errorMsg = response.message;
        showAlert(TYPE.ERROR, errorMsg);
      } else {
        user.loading = false;
        const errorCode = response.result.error;
        const errorMsg = t(`User.${errorCode}`) || t("User.DeleteFailed");
        showAlert(TYPE.ERROR, errorMsg);
      }
    }
  }
}

async function sendResetPasswordFromList(row: any) {
  const email = row.email || row.username;
  row.loading = true;
  const ok = await sendResetPassword(email);
  row.loading = false;

  if (ok) {
    const fresh = await getUserById(row.id);
    row.activityExpiry = fresh?.activityExpiry ?? row.activityExpiry;
  }
}

async function resetTotpFromList(row: any) {
  const email = row.email || row.username;
  row.loading = true;
  const ok = await resetTotpAndNotify(row.id, email);
  row.loading = false;

  if (ok) {
    const fresh = await getUserById(row.id);
    row.activityExpiry = fresh?.activityExpiry ?? row.activityExpiry;
  }
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createVNode(_unref(ElCard), {
      id: "users-card",
      class: "card border-0 oneTeg-card oneTeg-card-body"
    }, {
      default: _withCtx(() => [
        _createVNode(_unref(ElTable), {
          class: "oneTeg-table",
          data: userList.value,
          "empty-text": loadingUsers.value ? ' ' : 'No Data',
          "default-sort": { prop: sortField.value, order: sortAscending.value },
          onRowClick: goToEditPage,
          onSortChange: sortChanged,
          "row-class-name": 
          _unref(hasPermission)('USERS.LIST.EDIT')
            ? 'oneTeg-table-row-permission'
            : 'oneTeg-table-row-no-permission'
        ,
          height: "97%"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('Common.Name'),
              prop: "name",
              sortable: "custom",
              width: "auto"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("strong", {
                  id: 'name-label-' + scope.$index
                }, _toDisplayString(scope.row.name), 9, _hoisted_2)
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('User.LastName'),
              prop: "lastname",
              sortable: "custom",
              width: "auto"
            }, {
              default: _withCtx((scope) => [
                _createElementVNode("strong", {
                  id: 'lastname-label-' + scope.$index
                }, _toDisplayString(scope.row.lastname), 9, _hoisted_3)
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('Common.UserName'),
              prop: "username",
              sortable: "custom",
              width: "auto"
            }, null, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('Common.CreationTime'),
              prop: "createdAt",
              sortable: "custom",
              width: "auto"
            }, {
              default: _withCtx((scope) => [
                _createTextVNode(_toDisplayString(convertDate(scope.row.createdAt)), 1)
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), {
              label: _unref(t)('Common.Role'),
              prop: "authorities",
              width: "auto"
            }, {
              default: _withCtx((scope) => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(scope.row.authorities, (item) => {
                  return (_openBlock(), _createElementBlock("span", {
                    key: item.authority
                  }, [
                    (!item.deleted)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_4, _toDisplayString(item.authority), 1))
                      : (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString("No Role")))
                  ]))
                }), 128))
              ]),
              _: 1
            }, 8, ["label"]),
            _createVNode(_unref(ElTableColumn), { width: "300px" }, {
              header: _withCtx(() => [
                _createElementVNode("div", _hoisted_6, [
                  _createVNode(_unref(ElInput), {
                    id: "search-input",
                    modelValue: search.value,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((search).value = $event)),
                    size: "mini",
                    class: "oneTeg-search-input max-w-170",
                    onInput: _unref(onSearchInput),
                    placeholder: _unref(t)('Common.TypeToSearch')
                  }, null, 8, ["modelValue", "onInput", "placeholder"]),
                  _createVNode(_unref(ElDivider), {
                    direction: "vertical",
                    class: "v-divider-stretch",
                    "aria-hidden": "true"
                  }),
                  _createElementVNode("div", _hoisted_7, [
                    (_unref(hasPermission)('USERS.LIST.CREATE'))
                      ? (_openBlock(), _createBlock(_unref(ElButton), {
                          key: 0,
                          id: "create-button",
                          class: "no-border btn-oneTeg2-table-create btn-labeled",
                          size: "mini",
                          type: "primary",
                          onClick: _cache[1] || (_cache[1] = ($event: any) => (_ctx.$router.push('/users/create')))
                        }, {
                          default: _withCtx(() => [
                            _createElementVNode("span", _hoisted_8, [
                              _createElementVNode("span", _hoisted_9, [
                                _createVNode(_component_font_awesome_icon, { icon: "plus" })
                              ]),
                              _createElementVNode("span", _hoisted_10, _toDisplayString(_unref(t)("User.CreateUser")), 1)
                            ])
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true)
                  ])
                ])
              ]),
              default: _withCtx((scope) => [
                _createElementVNode("div", _hoisted_11, [
                  _createVNode(_unref(ElDropdown), null, {
                    dropdown: _withCtx(() => [
                      _createVNode(_unref(ElDropdownMenu), null, {
                        default: _withCtx(() => [
                          (_unref(hasPermission)('USERS.LIST.VIEW_DETAILS'))
                            ? (_openBlock(), _createBlock(_unref(ElDropdownItem), {
                                key: 0,
                                id: 'details-button-' + scope.$index,
                                onClick: ($event: any) => (_ctx.$router.push('/users/detail/' + scope.row.id))
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)("Common.Details")), 1)
                                ]),
                                _: 2
                              }, 1032, ["id", "onClick"]))
                            : _createCommentVNode("", true),
                          (_unref(hasPermission)('USERS.LIST.EDIT'))
                            ? (_openBlock(), _createBlock(_unref(ElDropdownItem), {
                                key: 1,
                                id: 'edit-button-' + scope.$index,
                                onClick: ($event: any) => (_ctx.$router.push('/users/edit/' + scope.row.id))
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)("Common.Edit")), 1)
                                ]),
                                _: 2
                              }, 1032, ["id", "onClick"]))
                            : _createCommentVNode("", true),
                          (_unref(hasPermission)('USERS.LIST.REMOVE'))
                            ? (_openBlock(), _createBlock(_unref(ElDropdownItem), {
                                key: 2,
                                id: 'remove-button-' + scope.$index,
                                onClick: ($event: any) => (deleteSelectedUser(scope.row))
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_unref(t)("Common.Delete")), 1)
                                ]),
                                _: 2
                              }, 1032, ["id", "onClick"]))
                            : _createCommentVNode("", true),
                          _createVNode(_unref(ElDropdownItem), {
                            id: 'reset-password-button-' + scope.$index,
                            disabled: scope.row.loading || scope.row.activityExpiry,
                            onClick: _withModifiers(($event: any) => (sendResetPasswordFromList(scope.row)), ["stop"])
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(t)("Common.ResetPassword")), 1)
                            ]),
                            _: 2
                          }, 1032, ["id", "disabled", "onClick"]),
                          _createVNode(_unref(ElDropdownItem), {
                            id: 'reset-totp-' + scope.$index,
                            disabled: scope.row.loading,
                            onClick: _withModifiers(($event: any) => (resetTotpFromList(scope.row)), ["stop"])
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(_unref(t)("Common.ResetTotp")), 1)
                            ]),
                            _: 2
                          }, 1032, ["id", "disabled", "onClick"])
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    default: _withCtx(() => [
                      _createVNode(_unref(ElButton), {
                        class: "no-border btn-oneTeg show-on-row-hover max-w-42-5",
                        size: "mini",
                        onClick: _cache[2] || (_cache[2] = _withModifiers(() => {}, ["stop"])),
                        id: 'cog-button-' + scope.$index,
                        type: "primary"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_font_awesome_icon, { icon: "cog" })
                        ]),
                        _: 2
                      }, 1032, ["id"])
                    ]),
                    _: 2
                  }, 1024),
                  (scope.row.loading)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
                        _createElementVNode("img", {
                          src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
                          alt: "Loading...",
                          class: "loader-logo-25"
                        }, null, 8, _hoisted_13)
                      ]))
                    : _createCommentVNode("", true)
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        }, 8, ["data", "empty-text", "default-sort", "row-class-name"]),
        _createElementVNode("div", _hoisted_14, [
          _createVNode(OneTegPagination, {
            "page-sizes": [10, 25, 50, 100],
            "page-size": perPageUsers.value,
            "onUpdate:pageSize": _cache[3] || (_cache[3] = ($event: any) => ((perPageUsers).value = $event)),
            total: numberOfUsers.value,
            "current-page": currentPageUsers.value,
            "onUpdate:currentPage": _cache[4] || (_cache[4] = ($event: any) => ((currentPageUsers).value = $event)),
            background: true,
            small: true,
            onSizeChange: sizeChanged,
            onCurrentChange: getUsers,
            layout: 'total, sizes, prev, pager, next'
          }, null, 8, ["page-size", "total", "current-page"])
        ])
      ]),
      _: 1
    }),
    _withDirectives(_createElementVNode("div", _hoisted_15, [
      _createElementVNode("img", {
        src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
        alt: "Loading...",
        class: "loader-logo-40"
      }, null, 8, _hoisted_16)
    ], 512), [
      [_vShow, loadingUsers.value]
    ])
  ]))
}
}

})